'use client';

import React from 'react';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';

interface Specialist {
  role: string;
  organisation: string;
  body: React.ReactNode;
  tagline?: string;
}

const specialists: Specialist[] = [
  {
    role: 'Architects',
    organisation: 'Initiate Architecture, Bridgend',
    tagline: 'A shared vision for preserving the past and creating a sustainable future.',
    body: (
      <>
        <p>Ross Hartland and the team at Initiate Architecture have developed a thoughtful, ambitious and genuinely conservation-led vision for the restoration of Coed Parc. Their approach combines a deep respect for the building's history with a clear vision for creating a sustainable future for it.</p>
        <p>A key part of their philosophy is to work with the existing building rather than against it — understanding how it was originally constructed, respecting its materials and allowing its history to inform the decisions being made today.</p>
        <p>The team has placed particular emphasis on using sustainable, appropriate and sympathetic materials, ensuring that new work complements the historic fabric of Coed Parc rather than competing with it.</p>
        <p>What has been particularly valuable to us is the level of personal commitment Ross brings to the project. He continues to make regular site visits and remains actively involved throughout the restoration, taking the time to look closely at the building, discuss issues as they arise and work with the wider team to find the right solutions.</p>
        <p>That hands-on involvement is incredibly important when working with a building as complex and individual as Coed Parc. Historic buildings often reveal something unexpected once work begins, and having an architect who is engaged, approachable and willing to work through those challenges on site makes a real difference.</p>
        <p>Initiate's vision is not simply to return Coed Parc to a particular moment in its history. It is to understand everything the building has become, preserve what makes it special and carefully shape its next chapter.</p>
        <p>We are incredibly grateful to Ross and the whole team at Initiate Architecture for the expertise, enthusiasm and commitment they are bringing to Coed Parc.</p>
      </>
    ),
  },
  {
    role: 'Structural Engineer',
    organisation: 'Spark Structures',
    body: (
      <>
        <p>Lloyd Evans of Spark Structures brings specialist structural engineering expertise to the restoration of Coed Parc, with a focus on understanding the building as it stands rather than treating it as a modern structure.</p>
        <p>Working alongside the architectural and conservation team, Lloyd is helping to unravel how Coed Parc was originally constructed, how it has evolved over more than a century and how its historic structure can be carefully repaired and strengthened.</p>
        <p>For a listed building, structural intervention needs to be considered with particular care. The aim is not simply to make the building structurally sound, but to achieve this while retaining as much of the original fabric and character as possible.</p>
        <p>The approach is an important part of the wider conservation philosophy at Coed Parc — finding practical and sympathetic solutions that respect the building's history while giving it the strength and stability it needs for the future.</p>
      </>
    ),
  },
  {
    role: 'Timber Specialist',
    organisation: 'Demaus Building Diagnostics Ltd',
    tagline: "Don't replace what can be repaired.",
    body: (
      <>
        <p>Robert Demaus brings specialist expertise in historic timber, building diagnostics and damp, helping the restoration team understand how Coed Parc's traditional construction has performed over more than a century.</p>
        <p>His work involves looking beyond the visible signs of deterioration to understand what is happening within the building — particularly where moisture, ventilation and the condition of historic timber interact.</p>
        <p>This specialist knowledge is essential when working with an historic building. Timber that may initially appear damaged or beyond repair can sometimes be carefully conserved once the underlying cause of the problem has been understood and addressed.</p>
        <p>Robert's detailed assessments help the team make informed decisions about what can be repaired, what should be retained and where intervention is genuinely necessary.</p>
        <p>This reflects one of the central principles of the Coed Parc restoration: <em>Don't replace what can be repaired.</em></p>
        <p>By understanding the building's traditional materials and how they work together, the aim is to preserve as much of Coed Parc's original fabric as possible while ensuring that it remains healthy and resilient for the future.</p>
      </>
    ),
  },
  {
    role: 'Conservation Officer — Bridgend County Council',
    organisation: 'Moira Lucas',
    tagline: 'Protecting the past, understanding the present and creating a future for Coed Parc.',
    body: (
      <>
        <p>Moira Lucas brings specialist conservation knowledge to the restoration of Coed Parc, helping the team understand and protect the qualities that make the building such an important part of Bridgend's heritage.</p>
        <p>Her role is particularly valuable in ensuring that decisions made during the restoration are informed by the building's architectural character, historic fabric and significance, while still allowing Coed Parc to have a practical and sustainable future.</p>
        <p>We greatly value Moira's knowledge, opinion and input throughout the project. Her thoughtful and constructive approach has been particularly important when considering how best to balance conservation with the practical challenges of restoring a building that has evolved over more than 125 years.</p>
        <p>Just as importantly, her approachability and willingness to engage with the project team has made her an invaluable part of the restoration. Being able to discuss ideas openly, consider different approaches and work towards the best solution for the building is hugely important on a project of this nature.</p>
        <p>Her involvement helps ensure that the building's special character remains at the heart of the restoration, while allowing Coed Parc to evolve and have a sustainable future.</p>
      </>
    ),
  },
  {
    role: 'Roofing, Heritage Craft and Conservation',
    organisation: 'AGA Conservation',
    tagline: 'Original materials. Traditional craftsmanship. A roof built to last.',
    body: (
      <>
        <p>Angelo Aviles of AGA Conservation brings specialist experience in heritage roofing, traditional construction and conservation craftsmanship to the project. The team's understanding of historic materials and techniques is particularly valuable when working on a building of Coed Parc's age, where preserving the original construction is as important as ensuring the roof performs well for the future.</p>
        <p>Wherever possible, we are retaining and reusing the original green Delabole slates that have formed part of Coed Parc's roof for generations. Where additional slates are needed, we are carefully sourcing reclaimed Delabole slates that closely match the originals in colour, size, thickness and age. This ensures that any new material blends naturally with the existing roof, preserving both its appearance and its historic character.</p>
        <p>The construction of the roof itself presents a fascinating specialist challenge. Coed Parc features hand-crafted mitered hips, a highly skilled traditional roofing detail where the slates meet precisely at the corners without the use of a conventional hip covering.</p>
        <p>These details require considerable skill, accuracy and an understanding of traditional roofing methods. They are an important part of what gives Coed Parc's roof its distinctive character and are being treated as an integral part of the building's heritage.</p>
        <p>The approach is therefore not simply to make the roof weather-tight, but to understand, repair and preserve the craftsmanship that went into creating it in the first place.</p>
        <p>Angelo and the team at AGA Conservation are bringing specialist knowledge, traditional skills and genuine care to this important part of the restoration.</p>
      </>
    ),
  },
];

export default function SpecialistsPage() {
  return (
    <main className="min-h-screen bg-background">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-10 bg-secondary/40 border-b border-border">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-xs tracking-widest uppercase text-primary mb-4">The Team</p>
          <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-foreground leading-tight mb-6">
            A Team of Specialists
          </h1>
          <p className="font-sans text-lg text-muted-foreground leading-relaxed mb-3">
            Restoring Coed Parc is a collaborative process.
          </p>
          <p className="font-sans text-base text-muted-foreground leading-relaxed">
            It brings together people with different areas of expertise, all working towards the same objective: to protect and restore an important historic building.
          </p>
        </div>
      </section>

      {/* Specialists */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto space-y-16">
          {specialists.map((s, i) => (
            <article
              key={i}
              className="border-t border-border pt-12"
            >
              {/* Role label */}
              <p className="font-sans text-xs tracking-widest uppercase text-primary mb-2">
                {s.role}
              </p>
              {/* Organisation / Name */}
              <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-foreground mb-6">
                {s.organisation}
              </h2>
              {/* Body copy */}
              <div className="font-sans text-base text-foreground/80 leading-relaxed space-y-4">
                {s.body}
              </div>
              {/* Tagline */}
              {s.tagline && (
                <p className="mt-8 font-serif text-lg italic text-primary border-l-2 border-primary pl-5">
                  {s.tagline}
                </p>
              )}
            </article>
          ))}
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}
